/**
 * Realtime chat channels / events.
 *
 * Confirmed backend contract (both channels are type: private, i.e.
 * subscribed via Laravel Echo.private(), which maps to Pusher's
 * "private-" prefix):
 *   message-context.{contextId}      -> message.sent, message.updated, message.reaction, message.reaction.removed
 *   user.direct-contacts.{userId}    -> direct.contact.updated
 *   user.chat-members.{userId}       -> chat.members.updated (empty signal → refetch my-members)
 *
 * When REACT_APP_PUSHER_SKIP_AUTH=true, the "private-" prefix is stripped
 * and /broadcasting/auth is never called (public channel, dev-only fallback
 * for when backend auth isn't available — the backend will never actually
 * broadcast to the public form of these channels).
 * When false (default expected), private Laravel channels are used with
 * backend auth via /broadcasting/auth.
 *
 * Override with env if the backend ever changes the template:
 *   REACT_APP_PUSHER_MESSAGE_CONTEXT_CHANNEL=message-context.{id}
 *   REACT_APP_PUSHER_DIRECT_CONTACTS_CHANNEL=user.direct-contacts.{id}
 *   REACT_APP_PUSHER_CHAT_MEMBERS_CHANNEL=user.chat-members.{id}
 */

import { isPusherAuthSkipped } from "../services/pusher"

const applyChannelTemplate = (template, id) =>
  String(template || "").replace(/\{id\}/g, String(id))

const asPrivate = name => {
  if (!name) return ""
  if (name.startsWith("private-") || name.startsWith("presence-")) return name
  return `private-${name}`
}

const asPublic = name => String(name || "").replace(/^(private-|presence-)/, "")

const addChannel = (names, value) => {
  const name = String(value || "").trim()
  if (!name) return
  if (
    isPusherAuthSkipped() &&
    (name.startsWith("private-") || name.startsWith("presence-"))
  ) {
    return
  }
  if (!names.includes(name)) names.push(name)
}

export const getDirectContactsChannelName = userId => {
  if (!userId) return ""
  const skipAuth = isPusherAuthSkipped()
  const template = process.env.REACT_APP_PUSHER_DIRECT_CONTACTS_CHANNEL
  const base = template
    ? applyChannelTemplate(template, userId)
    : `user.direct-contacts.${userId}`
  return skipAuth ? asPublic(base) : asPrivate(base)
}

/** Private channel: invite accepted / member activated → refresh DM list. */
export const getChatMembersChannelName = userId => {
  if (!userId) return ""
  const skipAuth = isPusherAuthSkipped()
  const template = process.env.REACT_APP_PUSHER_CHAT_MEMBERS_CHANNEL
  const base = template
    ? applyChannelTemplate(template, userId)
    : `user.chat-members.${userId}`
  return skipAuth ? asPublic(base) : asPrivate(base)
}

export const getRealtimeChannelNames = ({
  contextId,
  userId,
  conversationRaw,
} = {}) => {
  const names = []
  const skipAuth = isPusherAuthSkipped()

  const fromApi =
    conversationRaw?.channel ||
    conversationRaw?.pusher_channel ||
    conversationRaw?.broadcast_channel ||
    conversationRaw?.realtime_channel
  if (fromApi) {
    addChannel(names, skipAuth ? asPublic(fromApi) : asPrivate(fromApi))
  }

  // Backend broadcasts on message-context.{contextId} (public in debug /
  // skip-auth) and private-message-context.{contextId} when Echo.private()
  // is used. Subscribe to both so message.sent is never missed.
  if (contextId) {
    const template = process.env.REACT_APP_PUSHER_MESSAGE_CONTEXT_CHANNEL
    const base = template
      ? applyChannelTemplate(template, contextId)
      : `message-context.${contextId}`
    addChannel(names, asPublic(base))
    if (!skipAuth) addChannel(names, asPrivate(base))
  }

  // Sidebar-only: direct.contact.updated must not hit an open thread subscription.
  if (!contextId) {
    addChannel(names, getDirectContactsChannelName(userId))
  }

  return names
}

export const CLIENT_TYPING_EVENT = "client-typing"
export const TYPING_THROTTLE_MS = 2000
export const TYPING_EXPIRE_MS = 3000

/**
 * Frontend-triggered ("Option A") events. Require private channels +
 * backend /broadcasting/auth + "Client Events" enabled in the Pusher
 * app dashboard. See services/pusher.js for the auth wiring.
 */
export const CLIENT_MESSAGE_EVENT = "client-message"
export const CLIENT_MESSAGE_DELETED_EVENT = "client-message-deleted"
export const CLIENT_REACTION_EVENT = "client-reaction"
export const MESSAGE_SENT_EVENT = "message.sent"
export const MESSAGE_UPDATED_EVENT = "message.updated"
export const MESSAGE_REACTION_EVENT = "message.reaction"
export const MESSAGE_REACTION_REMOVED_EVENT = "message.reaction.removed"
export const DIRECT_CONTACT_UPDATED_EVENT = "direct.contact.updated"
/** Empty signal: client should refetch GET /api/v1/project-members/my-members. */
export const CHAT_MEMBERS_UPDATED_EVENT = "chat.members.updated"

export const eventNameAliases = name => {
  const event = String(name || "").trim()
  if (!event) return []
  const names = [event]
  if (!event.startsWith(".")) names.push(`.${event}`)
  return names
}

const parseJsonValue = value => {
  if (typeof value !== "string") return value
  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return value
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

export const unwrapRealtimePayload = payload => {
  const parsed = parseJsonValue(payload)
  if (!parsed || typeof parsed !== "object") return parsed
  if (parsed.message && typeof parsed.message === "object") {
    return unwrapRealtimePayload(parsed.message)
  }
  if (parsed.model && typeof parsed.model === "object") {
    return unwrapRealtimePayload(parsed.model)
  }
  if (parsed.data != null) {
    const nested = parseJsonValue(parsed.data)
    if (nested && typeof nested === "object") {
      return unwrapRealtimePayload(nested)
    }
  }
  if (parsed.result && typeof parsed.result === "object") {
    return unwrapRealtimePayload(parsed.result)
  }
  if (parsed.payload && typeof parsed.payload === "object") {
    return unwrapRealtimePayload(parsed.payload)
  }
  return parsed
}

export const parseDirectContactPayload = payload => {
  const contact = unwrapRealtimePayload(payload)
  if (!contact || typeof contact !== "object") return null
  const id = contact.id || contact.context_id || contact.conversation_id
  if (id == null || id === "") return null
  return { ...contact, id }
}

export const getRealtimeContextId = payload => {
  const data = unwrapRealtimePayload(payload) || {}
  return (
    payload?.context_id ||
    payload?.message_context_id ||
    payload?.contextId ||
    data.context_id ||
    data.message_context_id ||
    data.contextId ||
    ""
  )
}

export const getRealtimeMessageId = payload => {
  const data = unwrapRealtimePayload(payload) || {}
  return (
    data.id ||
    data.uuid ||
    data.message_id ||
    data.messageId ||
    payload?.id ||
    payload?.uuid ||
    ""
  )
}

export const looksLikeChatMessage = payload => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return false
  const id = data.id || data.uuid || data.message_id
  if (id == null || id === "") return false
  return Boolean(
    data.message != null ||
      data.body != null ||
      data.content != null ||
      data.sender ||
      data.user ||
      data.medias ||
      data.media ||
      data.attachments ||
      data.created_at ||
      data.sent_at,
  )
}

export const looksLikeTypingEvent = (eventName, payload) => {
  if (eventName === CLIENT_TYPING_EVENT) return true
  const name = String(eventName || "").toLowerCase()
  if (name.includes("typing")) return true
  const data = payload && typeof payload === "object" ? payload : {}
  return data.typing != null || data.is_typing != null
}

export const looksLikeReactionEvent = (eventName, payload) => {
  const name = String(eventName || "").toLowerCase()
  if (name.includes("reaction") || name.includes("reacted")) return true
  // message.sent includes reaction_summary: [] — an empty array is truthy
  // and used to swallow new messages as fake reaction events.
  if (looksLikeChatMessage(payload)) return false
  const data = unwrapRealtimePayload(payload) || {}
  const hasSummary =
    Array.isArray(data.reaction_summary) && data.reaction_summary.length > 0
  return Boolean(
    data.reaction_id ||
      hasSummary ||
      ((data.reaction || data.emoji) &&
        (data.message_id || data.messageId || data.id)),
  )
}

export const looksLikeMessageSentEvent = eventName => {
  const name = String(eventName || "")
    .toLowerCase()
    .replace(/^\./, "")
  return (
    name === MESSAGE_SENT_EVENT ||
    name === CLIENT_MESSAGE_EVENT ||
    name === "message.created" ||
    name.endsWith(".sent")
  )
}

export const looksLikeMessageUpdatedEvent = eventName => {
  const name = String(eventName || "")
    .toLowerCase()
    .replace(/^\./, "")
  if (name === DIRECT_CONTACT_UPDATED_EVENT) return false
  return (
    name === MESSAGE_UPDATED_EVENT ||
    name.includes("updat") ||
    name.includes("edit")
  )
}

export const looksLikeDeletedEvent = eventName =>
  String(eventName || "")
    .toLowerCase()
    .includes("delet")
